import { useState } from 'react';
import _ from 'lodash';
import { Button, Modal } from '@scale/scaleui';
import {
  IOnboardingFlow,
  OnboardingFlowNodeType,
  OnboardingStepType,
  ConditionType,
  LoadBalancerCondition,
} from '@scale/shared/onboardingControlPanel';
import { useOCPStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useOCPStore';
import { useDraftFlowStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useDraftFlowStore';
import { LoadBalancerRouting } from 'components/GrowthCenter/OnboardingControlPanel/components/LoadBalancer';
import { STEP_FIELDS } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/common';

interface LoadBalancerModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceNodeName: string;
}

export const LoadBalancerModal: React.FC<LoadBalancerModalProps> = ({
  isOpen,
  onClose,
  sourceNodeName,
}) => {
  const { currentFlow, detailTarget, setDetailTarget, setCurrentFlow } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    detailTarget: state.detailTarget,
    setDetailTarget: state.setDetailTarget,
    setCurrentFlow: state.setCurrentFlow,
  }));
  const { draftFlows, editDraftEntityValue } = useDraftFlowStore(state => ({
    draftFlows: state.draftFlows,
    editDraftEntityValue: state.editDraftEntityValue,
  }));
  const [originalFlow, setOriginalFlow] = useState<IOnboardingFlow | undefined>(undefined);

  const handleSubmit = () => {
    updateLoadBalancerConditions();
    onClose();
  };

  const handleCancel = () => {
    if (originalFlow) setCurrentFlow(originalFlow);
    onClose();
  };

  const updateLoadBalancerConditions = () => {
    if (!currentFlow) return;
    setOriginalFlow(currentFlow);

    // Fallback: If any edge type is not a LoadBalancerCondition, convert it to one (default is capacity undefined)
    const flow = draftFlows[currentFlow._id] ?? currentFlow;
    const possibleTransitions = flow.edges.filter(edge => edge.sourceNode === sourceNodeName);
    const nonLoadBalancerEdges = possibleTransitions.filter(
      edge => edge.conditions[0]?.type !== ConditionType.LoadBalancer,
    );

    nonLoadBalancerEdges.forEach(edge => {
      const conditions: LoadBalancerCondition[] = [
        {
          type: ConditionType.LoadBalancer,
          capacity: undefined,
          percentage: undefined,
        },
      ];
      const updatedEdge = {
        ...edge,
        conditions,
      };
      editDraftEntityValue(currentFlow._id, updatedEdge, updatedEdge);
    });

    const newNode = {
      ..._.pick(detailTarget, STEP_FIELDS),
      type: OnboardingFlowNodeType.Step,
      stepType: OnboardingStepType.LoadBalancer,
    };
    const targetNode = flow.nodes.find(node => node.name === sourceNodeName);
    if (!targetNode) {
      return;
    }
    const newDetailTarget = editDraftEntityValue(currentFlow._id, targetNode, newNode);

    setDetailTarget(newDetailTarget);
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleCancel}
      title={`Confirm Load Balancer Routing`}
      innerClassName="scaleui scale-tailwind scale-theme w-[50%]"
      actions={
        <div className="flex items-center gap-2 justify-end">
          <Button className="" onClick={handleCancel} size="sm" variant="neutral">
            Cancel
          </Button>
          <Button className="" onClick={handleSubmit} size="sm" variant="primary">
            Continue
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-2 py-2">
        <LoadBalancerRouting allowAddOrDeleteCondition={false} />
      </div>
    </Modal>
  );
};
